import React, { Component, createRef, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import { Subject } from 'rxjs/Subject';
import type { Subscription } from 'rxjs/Subscription';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { ButtonGroup } from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage } from '@atlassian/jira-intl';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { ENTITY_FIELD_KEYS } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import type { JiraAppLink } from '@atlassian/jira-issue-view-common-types/src/issue-type.tsx';
import { parseIssueUrl } from '@atlassian/jira-issue-view-common-utils/src/issue-url-parser/index.tsx';
import { smoothScrollIntoCenterIfNeeded } from '@atlassian/jira-issue-view-common-utils/src/scroll/index.tsx';
import { transformAppLink } from '@atlassian/jira-issue-view-common-views/src/app-link/app-link-select/utils.tsx';
import { IssueLinkLocalitySubscriber } from '@atlassian/jira-issue-view-services/src/issue-link-locality-service/context.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { IssueLinkTypeSelect, type SelectItemType } from '../issue-links-type/index.tsx';
import type { SelectItemAppLink } from '../jira-app-links/types.tsx';
import AppLinksSelect from '../jira-app-links/view/index.tsx';
import type { IssueLink, LinkedIssueType } from '../types.tsx';

import {
	LOCAL_JIRA_APP_LINK,
	ISSUE_SEARCH_DEBOUNCE_TIME,
	ISSUE_SEARCH_DEBOUNCE_TIME_NEW,
} from './constant.tsx';
import CreateLinkedIssueButton from './create-linked-issue-button/index.tsx';
import { CreateReciprocalLink } from './create-reciprocal-link/index.tsx';
import { fetchPossibleIssues, type IssueLinkSuggestion } from './issue-link-server.tsx';
import IssueSearchSelect from './issue-links-search/index.tsx';
import messages from './messages.tsx';
import {
	getLinkedIssue,
	getItsmPractice,
	getProjectType,
} from './services/fetch-linked-issue/index.tsx';
import { transformIssueLink, transformSaveRemoteIssueLinkPayload } from './transformer.tsx';
import {
	type InvalidSelection,
	type NewIssueLinksType,
	type Props,
	type State,
	SELECTION_INVALID,
	SELECTION_SELF_LINK,
	SELECTION_UNAUTHENTICATED,
	SELECTION_OTHER_APPLINK,
	type NewIssueLinkWrapper,
} from './types.tsx';

const SINGLE_COLUMN_MAX_WIDTH = 480;

const getInitialSearchState = () => ({
	newIssueLinks: [],
	isDisabled: true,
	invalidSelections: [],
	invalidSelectionsWithReason: [],
});

const getInvalidIssuesMessage = (intl: IntlShape, issueKeys: string[]): string => {
	if (issueKeys.length === 1) {
		return intl.formatMessage(
			fg('jira-issue-terminology-refresh-m3')
				? messages.invalidIssueIssueTermRefresh
				: messages.invalidIssue,
			{ issueKey: issueKeys[0] },
		);
	}
	if (issueKeys.length === 2) {
		return intl.formatMessage(
			fg('jira-issue-terminology-refresh-m3')
				? messages.twoInvalidIssuesIssueTermRefresh
				: messages.twoInvalidIssues,
			{
				issueKeys: issueKeys.join(', '),
			},
		);
	}
	if (issueKeys.length >= 3) {
		return intl.formatMessage(
			fg('jira-issue-terminology-refresh-m3')
				? messages.multipleInvalidIssuesIssueTermRefresh
				: messages.multipleInvalidIssues,
			{
				firstTwoIssueKeys: issueKeys.slice(0, 2).join(', '),
				countRemainingIssues: issueKeys.length - 2,
			},
		);
	}
	return '';
};

export const getErrorMessage = (
	intl: IntlShape,
	invalidSelections: InvalidSelection[],
): string | undefined => {
	if (isEmpty(invalidSelections)) return undefined;

	let errorKey = invalidSelections[0].reason;
	if (invalidSelections.some(({ reason }) => reason === SELECTION_INVALID)) {
		errorKey = SELECTION_INVALID;
	} else if (invalidSelections.some(({ reason }) => reason === SELECTION_UNAUTHENTICATED)) {
		errorKey = SELECTION_UNAUTHENTICATED;
	}

	const invalidSelectionsForErrorKey = invalidSelections.filter(
		(invalid) => invalid.reason === errorKey,
	);

	const jiraAppLinkName: string | undefined = invalidSelectionsForErrorKey
		.filter((invalidSelection) => invalidSelection?.jiraAppLink?.requireCredentials || false)
		.map((invalidSelection) => invalidSelection?.jiraAppLink?.name)
		.pop();

	switch (errorKey) {
		case SELECTION_SELF_LINK:
			return intl.formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.selfLinkInvalidIssueIssueTermRefresh
					: messages.selfLinkInvalidIssue,
				{
					issueKey: invalidSelections[0].value,
				},
			);
		case SELECTION_OTHER_APPLINK:
			return intl.formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.otherAppLinkIssueTermRefresh
					: messages.otherAppLink,
			);
		case SELECTION_UNAUTHENTICATED:
			return jiraAppLinkName
				? intl.formatMessage(messages.unauthenticatedAppLinkWithSiteName, {
						appLinkName: jiraAppLinkName,
					})
				: intl.formatMessage(messages.unauthenticatedAppLink);
		case SELECTION_INVALID:
			return getInvalidIssuesMessage(
				intl,
				invalidSelectionsForErrorKey.map((invalid) => invalid.value),
			);
		default:
			return undefined;
	}
};
export const transformJiraAppLinksForSelect = (
	jiraAppLinks: JiraAppLink[],
): SelectItemAppLink[] => {
	const remoteLinks = jiraAppLinks.map<SelectItemAppLink>((item) => transformAppLink(item));
	return [LOCAL_JIRA_APP_LINK, ...remoteLinks];
};

// eslint-disable-next-line jira/react/no-class-components
export class AddIssueLinkView extends Component<Props, State> {
	static isLinkedIssueInComponentState = (
		componentStateIssueLinks: State['newIssueLinks'],
		reduxStateIssueLink: Props['quickAddClickedIssue'],
	): boolean => {
		// no Issue Links in Redux means the component state is up-to-date
		if (!reduxStateIssueLink) {
			return true;
		}

		return componentStateIssueLinks.some(
			(issueLink) => issueLink.appLinkId === undefined && issueLink.id === reduxStateIssueLink?.id,
		);
	};

	static displayName = 'AddIssueLinkView';

	static defaultProps = {
		issueLinks: null,
		quickAddClickedCount: 0,
		containerWidth: null,
		onAdd: noop,
		onAddRemote: noop,
		onClose: noop,
	};

	constructor(props: Props) {
		super(props);
		this.state = this.getInitialState();
		this.query$ = new Subject();
		this.subscription = this.query$
			.flatMap(({ queryIssueKey, remoteAppLink }) =>
				fetchPossibleIssues(props.baseUrl, props.issueKey, queryIssueKey, remoteAppLink).map(
					(results) => ({
						issue: queryIssueKey,
						results,
					}),
				),
			)
			.subscribe(this.handleUpdate);
	}

	getInitialState = () => ({
		issueLinkType: this.transformIssueLinkTypesForSelect()[0],
		jiraAppLink: LOCAL_JIRA_APP_LINK,
		createReciprocalLink: true,
		...getInitialSearchState(),
	});

	static getDerivedStateFromProps(nextProps: Props, state: State): Partial<State> | null {
		// The redux store can make a request to add a linked issue to the unsubmitted issue-links in component state.
		// The code below pulls any such requested linked issue from redux into the component state.
		// When converting to a functional component please consider managing the unsubmitted links via a sweet state store instead of component state.
		if (
			!AddIssueLinkView.isLinkedIssueInComponentState(
				state.newIssueLinks,
				nextProps.quickAddClickedIssue,
			)
		) {
			return {
				isDisabled: false,
				newIssueLinks: [
					...state.newIssueLinks,
					{
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						content: nextProps.quickAddClickedIssue!.value,
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						id: nextProps.quickAddClickedIssue!.id,
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						value: nextProps.quickAddClickedIssue!.value,
						elemBefore: null,
						fullObject: nextProps.quickAddClickedIssue?.fullObject,
						filterValues: [],
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						iconUrl: nextProps.quickAddClickedIssue!.iconUrl,
						appLinkId: undefined,
					},
				],
			};
		}
		return null;
	}

	componentDidMount() {
		if (this.addLinkContainer) {
			const addLinkContainer = this.addLinkContainer;
			requestAnimationFrame(() => {
				smoothScrollIntoCenterIfNeeded(addLinkContainer);
				this.setFocusToIssueSearch();
			});
		}
		if (
			this.props.canLinkRemoteIssues &&
			(this.props.jiraAppLinksFetchStatus === 'NOT_LOADED' ||
				this.props.jiraAppLinksFetchStatus === 'ERROR')
		) {
			this.props.fetchJiraAppLinks();
		}
		if (fg('one_event_rules_them_all_fg')) {
			const analytics = this.props.createAnalyticsEvent({});
			getUpdateAnalyticsFlowHelper().fireAnalyticsStart(ENTITY_FIELD_KEYS.ISSUE_LINKS, {
				analytics,
				attributes: {
					fieldType: ENTITY_FIELD_KEYS.ISSUE_LINKS,
				},
			});
		}
	}

	componentDidUpdate(prevProps: Props) {
		if (
			this.addLinkContainer &&
			prevProps.quickAddClickedCount !== this.props.quickAddClickedCount
		) {
			smoothScrollIntoCenterIfNeeded(this.addLinkContainer);
			this.setFocusToIssueSearch();
		}

		// The redux store can make a request to add a linked issue to the unsubmitted issue-links in component state.
		// This checks if that issue has been picked up and clears out that part of the redux store if it has been.
		if (
			AddIssueLinkView.isLinkedIssueInComponentState(
				this.state.newIssueLinks,
				this.props.quickAddClickedIssue,
			)
		) {
			this.props.onClearRelatedIssueLink();
		}
	}

	componentWillUnmount() {
		this.subscription.unsubscribe();
	}

	private setFocusToIssueSearch = () => {
		// Set focus to select after smoothScrollIntoCenterIfNeeded animation is completed
		fg('jfp_a11y_team_link_issues_focus') &&
			setTimeout(() => this.issueSearchSelectRef.current?.focus(), 200);
	};

	issueSearchSelectRef = createRef<AtlaskitSelectRefType>();

	isLocalJira = (jiraAppLink: SelectItemAppLink) => jiraAppLink.value === LOCAL_JIRA_APP_LINK.value;

	getRemoteAppLink = (): JiraAppLink | undefined => {
		const { jiraAppLink } = this.state;
		return this.isLocalJira(jiraAppLink) ? undefined : jiraAppLink.fullObject;
	};

	onIssueLinkTypeChange = (item: SelectItemType) => this.setState({ issueLinkType: item });

	onJiraAppLinkChange = (
		item: SelectItemAppLink,
		setIsLinkingToLocalJira: (isLinkingToLocalJira: boolean) => void | Promise<void>,
	) => {
		const isLinkingToLocalJira = this.isLocalJira(item);
		setIsLinkingToLocalJira(isLinkingToLocalJira);
		this.setState({
			jiraAppLink: item,
			createReciprocalLink: !isLinkingToLocalJira,
			...getInitialSearchState(),
		});
	};

	getInvalidSelectionForIssuesWithDifferentAppLinks = (
		newIssueLinks: NewIssueLinkWrapper[],
	): InvalidSelection | undefined => {
		if (
			newIssueLinks.some((value) => !isEmpty(value.issueLink.fullObject)) &&
			newIssueLinks.some(
				(value) =>
					!isEmpty(value.appLink) &&
					value.appLink.fullObject.baseUrl !== this.state.jiraAppLink.fullObject.baseUrl,
			)
		) {
			return {
				value: '',
				reason: SELECTION_OTHER_APPLINK,
			};
		}
		return undefined;
	};

	getInvalidSelectionForUnauthenticatedAppLink = (
		newIssueLinks: NewIssueLinkWrapper[],
	): InvalidSelection | undefined => {
		const unauthenticatedIssueLink = newIssueLinks
			.filter((value) => value.appLink?.fullObject?.requireCredentials)
			.shift();
		if (unauthenticatedIssueLink) {
			return {
				value: unauthenticatedIssueLink.issueKey,
				jiraAppLink: unauthenticatedIssueLink?.appLink?.fullObject,
				reason: SELECTION_UNAUTHENTICATED,
			};
		}
		return undefined;
	};

	getInvalidSelectionForSelfLink = (
		newIssueLinks: NewIssueLinkWrapper[],
	): InvalidSelection | undefined => {
		if (newIssueLinks.some((issueLink) => this.props.issueKey === issueLink.issueKey)) {
			return {
				value: this.props.issueKey,
				reason: SELECTION_SELF_LINK,
			};
		}
		return undefined;
	};

	/** creates NewIssueLinkWrapper out of the new issue links handling issue keys and urls */
	getNewIssueLinksWrapperFrom = (newIssueLinks: NewIssueLinksType[]): NewIssueLinkWrapper[] => {
		const { jiraAppLinks } = this.props;
		const jiraAppLinksForSelect: SelectItemAppLink[] = transformJiraAppLinksForSelect(jiraAppLinks);
		return newIssueLinks.map((issueLink: NewIssueLinksType) => {
			if (isEmpty(issueLink.fullObject)) {
				const issueLinkUrl = parseIssueUrl(issueLink.value);
				const selectedAppLink =
					issueLinkUrl &&
					jiraAppLinksForSelect
						.filter((appLink) => appLink.fullObject.baseUrl === issueLinkUrl.host)
						.shift();
				return {
					issueLink,
					issueKey: selectedAppLink && issueLinkUrl ? issueLinkUrl.issueKey : issueLink.value,
					appLink: selectedAppLink,
				};
			}

			return {
				issueLink,
				issueKey: issueLink.value,
			};
		});
	};

	getCommonAppLinkFromIssueCandidates = (issueLinksWrapper: NewIssueLinkWrapper[]) =>
		issueLinksWrapper.length === 1 &&
		issueLinksWrapper[0].appLink?.fullObject?.requireCredentials === false &&
		issueLinksWrapper[0].appLink;

	onIssueSearchChange = (newIssueLinks: NewIssueLinksType[]) => {
		const submitServerRequests: Array<() => unknown> = [];
		const formattedValues: NewIssueLinkWrapper[] = this.getNewIssueLinksWrapperFrom(newIssueLinks);

		// currentInvalid is used to address one error at a time
		const currentInvalid: InvalidSelection | undefined =
			this.getInvalidSelectionForIssuesWithDifferentAppLinks(formattedValues) ||
			this.getInvalidSelectionForUnauthenticatedAppLink(formattedValues) ||
			this.getInvalidSelectionForSelfLink(formattedValues);

		const newSelectedAppLink =
			this.getCommonAppLinkFromIssueCandidates(formattedValues) || this.state.jiraAppLink;

		const updatedIssueLinks = formattedValues.map((issueLinkWrapper: NewIssueLinkWrapper) => {
			const { issueLink, issueKey, appLink } = issueLinkWrapper;

			const canBeValidatedInAppLink =
				isEmpty(appLink) || appLink.fullObject.baseUrl === newSelectedAppLink.fullObject.baseUrl;

			if (!issueLink.fullObject && canBeValidatedInAppLink) {
				if (!currentInvalid || currentInvalid.value !== issueKey) {
					submitServerRequests.push(() =>
						this.query$.next({
							queryIssueKey: issueKey,
							remoteAppLink: this.isLocalJira(newSelectedAppLink)
								? undefined
								: newSelectedAppLink.fullObject,
						}),
					);
				}
			}

			return {
				...issueLink,
				value: canBeValidatedInAppLink ? issueKey : issueLink.value,
				content: canBeValidatedInAppLink ? issueKey : issueLink.content,
				tag: { elemBefore: issueLink.elemBefore },
			};
		});

		const isDisabled = !!(currentInvalid || isEmpty(newIssueLinks));

		this.setState({
			newIssueLinks: updatedIssueLinks,
			isDisabled,
			invalidSelectionsWithReason: (currentInvalid && [currentInvalid]) || [],
			jiraAppLink: newSelectedAppLink,
		});

		submitServerRequests.map((request) => request()); // submit after setState incase this is a synchronous call
	};

	/* returns the update invalid selection list that only contains values still present in the user selections */
	updateInvalidSelections = (selectionValues: NewIssueLinksType[]): string[] => {
		const issueKeyValues = selectionValues ? selectionValues.map((v) => v.value) : [];
		return this.state.invalidSelections.filter((i) => issueKeyValues.includes(i));
	};

	onCreateReciprocalLinkChange = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		const newState = !this.state.createReciprocalLink;
		this.setState({ createReciprocalLink: newState });
		fireUIAnalytics(analyticsEvent, 'createReciprocalLink', {
			isChecked: newState,
		});
	};

	onShowReciprocalLinkTooltip = (analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'createReciprocalLink');
	};

	onSave = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		const { issueLinkType, newIssueLinks, jiraAppLink, createReciprocalLink } = this.state;
		const { issueLinks, isOpenedFromJsmSimilarIssues } = this.props;
		const isLinkingLocalIssue = this.isLocalJira(jiraAppLink);
		fireUIAnalytics(analyticsEvent, 'addLinkedIssueButton', {
			isRemote: !isLinkingLocalIssue,
			newLinkedIssueIds: newIssueLinks.map((newIssue) => newIssue.id),
			isOpenedFromJsmSimilarIssues,
		});

		if (fg('one_event_rules_them_all_fg')) {
			getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(ENTITY_FIELD_KEYS.ISSUE_LINKS, {
				analytics: analyticsEvent,
				attributes: {
					fieldType: ENTITY_FIELD_KEYS.ISSUE_LINKS,
					actionTaken: 'add',
				},
			});
		}
		if (issueLinkType && newIssueLinks && newIssueLinks.length > 0) {
			if (isLinkingLocalIssue) {
				this.saveLocalIssueLink(issueLinkType, newIssueLinks, issueLinks);
			} else {
				this.saveRemoteIssueLink(
					issueLinkType,
					newIssueLinks,
					jiraAppLink,
					issueLinks,
					createReciprocalLink,
				);
			}
		}
	};

	saveLocalIssueLink = (
		issueLinkType: SelectItemType,
		newIssueLinks: NewIssueLinksType[],
		issueLinks: IssueLink | null,
	) => {
		const { createAnalyticsEvent, isOpenedFromJsmSimilarIssues, onAddError, onAddSuccess } =
			this.props;
		const issueLinkTypeName = issueLinkType.fullObject && issueLinkType.fullObject.label;
		const existingIssueLinks = (issueLinks && issueLinks[issueLinkTypeName]) || [];
		const existingIssueKeys = existingIssueLinks
			.filter((link) => !link.isRemote)
			.map((link) => link.linkedIssueKey);

		newIssueLinks
			.map((issueLink) => {
				if (!issueLink.fullObject) {
					return {
						...issueLink,
						fullObject: { key: issueLink.value },
					};
				}
				return issueLink;
			})
			.filter((issueLink) => !existingIssueKeys.includes(issueLink.fullObject.key))
			.forEach(async (issueLink, index) => {
				const optimisticId = `OPTIMISTIC_LINKED_ISSUE_${Date.now()}_${index}`;
				const issueLinkFullObject = issueLink.fullObject;
				const linkedIssue = await getLinkedIssue(issueLink.value);
				issueLinkFullObject.issueProjectType = getProjectType(linkedIssue);
				issueLinkFullObject.issueItsmPractice = getItsmPractice(linkedIssue);
				if (fg('operandi_issue_view_additional_logging')) {
					this.props.onAdd(
						{
							optimisticId,
							linkedIssueKey: issueLink.fullObject.key,
							issueLinkType: issueLinkType.fullObject,
							issueLinks: transformIssueLink(
								optimisticId,
								issueLinkType.fullObject,
								issueLinkFullObject,
								this.props.baseUrl,
							),
							isOpenedFromJsmSimilarIssues,
						},
						createAnalyticsEvent({
							action: 'succeeded',
							isOpenedFromJsmSimilarIssues,
						}),
						createAnalyticsEvent({
							action: 'linkedToProject',
						}),
						onAddSuccess,
						onAddError,
					);
				} else {
					// @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
					this.props.onAdd(
						{
							optimisticId,
							linkedIssueKey: issueLink.fullObject.key,
							issueLinkType: issueLinkType.fullObject,
							issueLinks: transformIssueLink(
								optimisticId,
								issueLinkType.fullObject,
								issueLinkFullObject,
								this.props.baseUrl,
							),
							isOpenedFromJsmSimilarIssues,
						},
						createAnalyticsEvent({
							action: 'succeeded',
							isOpenedFromJsmSimilarIssues,
						}),
						onAddSuccess,
						onAddError,
					);
				}
			});
		this.props.onClose();
	};

	saveRemoteIssueLink = (
		issueLinkType: SelectItemType,
		newIssueLinks: NewIssueLinksType[],
		jiraAppLink: SelectItemAppLink,
		issueLinks: IssueLink | null,
		createReciprocalLink: boolean,
	) => {
		const { createAnalyticsEvent, onAddError, onAddSuccess } = this.props;
		const existingIssueGlobalIds = issueLinks
			? Object.keys(issueLinks) // eslint-disable-next-line @typescript-eslint/no-explicit-any
					.reduce<Array<any>>( // eslint-disable-next-line jira/js/no-reduce-accumulator-spread
						(acc: LinkedIssueType[], curr: string) => [...acc, ...issueLinks[curr]],
						[],
					)
					.filter((link) => link.isRemote)
					.map((link) => link.globalId)
			: [];

		newIssueLinks
			.filter((issueLink) => issueLink.fullObject) // invalid remoteIssueLink
			.map((issueLink, index) =>
				transformSaveRemoteIssueLinkPayload(
					index,
					issueLink,
					jiraAppLink.fullObject,
					issueLinkType,
					createReciprocalLink,
				),
			)
			.filter(
				(saveRemoteIssueLinkPayload) =>
					!saveRemoteIssueLinkPayload.globalId ||
					!existingIssueGlobalIds.includes(saveRemoteIssueLinkPayload.globalId),
			)
			.forEach((payload) => {
				// @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
				this.props.onAddRemote(
					payload,
					createAnalyticsEvent({
						action: 'succeeded',
						isRemote: true,
						withReciprocalLink: Boolean(payload.createReciprocalLink),
					}),
					onAddSuccess,
					onAddError,
				);
			});
		this.props.onClose();
	};

	onCancelKeyPress = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			const linkIssueButton = this.props.linkIssueRef;
			linkIssueButton?.focus();
		}
	};

	onClose = () => {
		if (fg('one_event_rules_them_all_fg')) {
			getUpdateAnalyticsFlowHelper().endSession(ENTITY_FIELD_KEYS.ISSUE_LINKS);
		}
		this.props.onClose();
	};

	handleUpdate = ({ issue, results }: { issue: string; results: IssueLinkSuggestion[] }) => {
		// This validation happens after the pre-validations before searching for issues.
		// When there is no result for the current issue, it means that the issue is invalid.
		// The invalid issue is concatenated to the current list of invalid selections.
		if (isEmpty(results)) {
			this.setState((currentState) => {
				const newIssueLinks = currentState.newIssueLinks.slice();
				const isDisabled = true;

				const invalidSelectionsWithReason = [
					...currentState.invalidSelectionsWithReason,
					{ value: issue, reason: SELECTION_INVALID } as const,
				];

				return { newIssueLinks, isDisabled, invalidSelectionsWithReason };
			});
		} else {
			const validServerIssueLink = results
				.map((val) => ({
					...val,
					tag: { elemBefore: val.elemBefore },
				}))
				.pop();

			if (!validServerIssueLink) {
				return;
			}

			this.setState((currentState) => {
				const newIssueLinks = currentState.newIssueLinks.map((link) =>
					link.value.toLowerCase() === validServerIssueLink.value.toLowerCase()
						? validServerIssueLink
						: link,
				);
				return { newIssueLinks };
			});
		}
	};

	transformIssueLinkTypesForSelect = (): SelectItemType[] =>
		this.props.issueLinkTypes.map<SelectItemType>((item) => ({
			label: item.label,
			value: item.id + item.direction,
			fullObject: item,
		}));

	subscription: Subscription;

	query$: Subject<{
		queryIssueKey: string;
		remoteAppLink?: JiraAppLink;
	}>;

	addLinkContainer: HTMLDivElement | null | undefined;

	addLinkContainerRef = (ref: HTMLDivElement | null) => {
		this.addLinkContainer = ref;
	};

	renderLoading = () => <Spinner />;

	renderFullAddForm() {
		const {
			intl,
			baseUrl,
			issueKey,
			containerWidth,
			canCreateLinkedIssue,
			canLinkRemoteIssues,
			jiraAppLinks,
		} = this.props;

		const { jiraAppLink, issueLinkType, newIssueLinks, isDisabled, invalidSelectionsWithReason } =
			this.state;

		const issueLinkTypeSelectItems = this.transformIssueLinkTypesForSelect();
		const jiraAppLinkSelectItems = transformJiraAppLinksForSelect(jiraAppLinks);
		/**
		 * This is different to the "global" Bento `isCompact` mode. This looks at its own
		 * `containerWidth` & can be put into compact mode even when the rest of Bento is not.
		 */
		const isCompact = Boolean(containerWidth && containerWidth <= SINGLE_COLUMN_MAX_WIDTH);
		const hasJiraAppLinks = jiraAppLinkSelectItems.length > 1;
		const shouldShowCreateLinkedIssueButton =
			this.isLocalJira(this.state.jiraAppLink) && canCreateLinkedIssue;

		const issueViewAddIssueLinkTestId =
			'issue.issue-view.views.issue-base.content.issue-links.add.issue-links-add-view.create-linked-issue';

		const createLinkedIssueContainer = isVisualRefreshEnabled() ? (
			<Box xcss={linkedIssueContainerIssueRefreshStyles} testId={issueViewAddIssueLinkTestId}>
				<CreateLinkedIssueButton />
			</Box>
		) : (
			<CreateLinkedIssueButtonContainer data-testid={issueViewAddIssueLinkTestId}>
				<CreateLinkedIssueButton />
			</CreateLinkedIssueButtonContainer>
		);

		return (
			<div ref={this.addLinkContainerRef}>
				<InputsContainer isCompact={isCompact}>
					{canLinkRemoteIssues && hasJiraAppLinks && (
						<SelectContainer isCompact={isCompact}>
							<IssueLinkLocalitySubscriber>
								{/* Access to setIsLinkingToLocalJira is needed, which would normally be done via a hook, but this
                                is a React Class component which does not support hooks. */}
								{(_, { setIsLinkingToLocalJira }) => (
									<AppLinksSelect
										selectItems={jiraAppLinkSelectItems}
										value={jiraAppLink}
										onChange={(e: SelectItemAppLink) =>
											this.onJiraAppLinkChange(e, setIsLinkingToLocalJira)
										}
										containerWidth={containerWidth || undefined}
										selectType="JiraAppLinksSelect"
									/>
								)}
							</IssueLinkLocalitySubscriber>
						</SelectContainer>
					)}
					<SelectContainer
						isCompact={isCompact}
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						data-testid="issue.views.issue-base.content.issue-links.add.select-container"
					>
						<IssueLinkTypeSelect
							selectItems={issueLinkTypeSelectItems}
							selectedItem={issueLinkType}
							onChange={this.onIssueLinkTypeChange}
						/>
					</SelectContainer>
					<IssueSearchContainer
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						data-testid="issue.views.issue-base.content.issue-links.add.issue-search-container"
					>
						<IssueSearchSelect
							value={newIssueLinks}
							baseUrl={baseUrl}
							issueKey={issueKey}
							onChange={this.onIssueSearchChange}
							remoteAppLink={this.getRemoteAppLink()}
							invalidMessage={getErrorMessage(intl, invalidSelectionsWithReason)}
							debounceFetchSuggestionsTime={
								fg('ces-57160-bump-debounce-time')
									? ISSUE_SEARCH_DEBOUNCE_TIME_NEW
									: ISSUE_SEARCH_DEBOUNCE_TIME
							}
							issueLinkType={this.state.issueLinkType}
							{...(fg('jfp_a11y_team_link_issues_focus') && {
								selectRef: this.issueSearchSelectRef,
							})}
						/>
					</IssueSearchContainer>
				</InputsContainer>
				<ButtonsContainer>
					{!this.isLocalJira(this.state.jiraAppLink) && (
						<CreateReciprocalLink
							isChecked={this.state.createReciprocalLink}
							onChange={this.onCreateReciprocalLinkChange}
							onShow={this.onShowReciprocalLinkTooltip}
						/>
					)}
					{shouldShowCreateLinkedIssueButton && createLinkedIssueContainer}
					<ButtonGroup>
						{}
						<div
							// eslint-disable-next-line jira/integration/test-id-by-folder-structure
							data-testid="issue.issue-view.views.issue-base.content.issue-links.add.issue-links-add-view.link-button"
						>
							<Button
								appearance="primary"
								onClick={this.onSave}
								isDisabled={isDisabled}
								interactionName="auto-generated.jira-issue-view-base.content.issue-links.add.button"
							>
								<FormattedMessage id="issue.issue-links-add-button-label" defaultMessage="Link" />
							</Button>
						</div>
						<Button
							appearance="subtle"
							onClick={this.onClose}
							onKeyPress={this.onCancelKeyPress}
							/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
							testId="issue.views.issue-base.content.issue-links.add.button"
							interactionName="auto-generated.issue.views.issue-base.content.issue-links.add.button.button1"
						>
							<FormattedMessage
								id="issue.issue-links-cancel-button-label"
								defaultMessage="Cancel"
							/>
						</Button>
					</ButtonGroup>
				</ButtonsContainer>
			</div>
		);
	}

	render() {
		if (
			this.props.canLinkRemoteIssues &&
			(this.props.jiraAppLinksFetchStatus === 'LOADING' ||
				this.props.jiraAppLinksFetchStatus === 'NOT_LOADED')
		) {
			return this.renderLoading();
		}
		return this.renderFullAddForm();
	}
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InputsContainer = styled.div<{ isCompact: boolean }>({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	flexDirection: ({ isCompact }) => (isCompact ? 'column' : 'row'),
	justifyContent: 'space-around',
	alignContent: 'stretch',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> *': {
		marginTop: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> :not(:last-child)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		marginRight: ({ isCompact }) => (isCompact ? '0' : token('space.100')),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueSearchContainer = styled.div({
	flex: 1,
	/* Needed for IE11, or else the second child has zero height when the flex
	 * container is using 'flex-direction: column'. */
	flexBasis: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonsContainer = styled.div({
	marginTop: token('space.100'),
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'flex-end',
	marginLeft: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CreateLinkedIssueButtonContainer = styled.div({
	flexGrow: 1,
	marginLeft: token('space.negative.150'),
});

const linkedIssueContainerIssueRefreshStyles = xcss({
	flexGrow: 1,
	marginLeft: 'space.negative.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectContainer = styled.div<{ isCompact: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth: ({ isCompact }) => (isCompact ? 'none' : '200px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ isCompact }) => (isCompact ? '100%' : '27%'),
	minWidth: '136px',
});
